/**
 * MY — Beneficial Owner Letters Appendix A/B/C (M32109) Register Detail 매핑.
 * 문서(슬롯)별 인물을 탭으로 표시 — 비서/디렉터 다건 Register Detail 과 동일 UX.
 */
import {
  registerRegisterDetail,
  type RegisterDetailRow,
  type RegisterDetailTabPanel,
} from '@/features/startTask/preset_task/shared/preset/register/registerDetailRegistry';
import {
  collectBoAppendixRegisterSlots,
  docLabelForBoAppendix,
  type BoAppendixRegisterSlot,
} from './boAppendixRegisterSlots';

function truthyFlag(v: unknown): boolean {
  const s = String(v ?? '').trim();
  return v === true || v === 1 || s === '1' || s === '☒'
    || s.toLowerCase() === 'true' || s.toLowerCase() === 'yes';
}

function pctLabel(raw: unknown): string {
  const s = String(raw ?? '').replace(/%/g, '').trim();
  return s ? `${s}%` : '';
}

function formatAddr(addr: BoAppendixRegisterSlot['residentialAddress']): string {
  if (!addr) return '';
  const line = [addr.street01, addr.street02, addr.street03].filter(Boolean).join(', ');
  const rest = [addr.city, addr.state, addr.post_code, addr.country].filter(Boolean).join(', ');
  return [line, rest].filter(Boolean).join(' · ');
}

function formatCriteriaSummary(slot: BoAppendixRegisterSlot): string {
  const cat = slot.criteria || {};
  const typeParts: string[] = [];
  if (truthyFlag(cat.cat_direct)) typeParts.push('Direct ownership');
  if (truthyFlag(cat.cat_indirect)) typeParts.push('Indirect ownership');
  if (truthyFlag(cat.cat_both)) typeParts.push('Direct & Indirect ownership');
  if (truthyFlag(cat.cat_control)) typeParts.push('Control by other means');

  const critParts: string[] = [];
  if (truthyFlag(cat.crit_direct_shares) || truthyFlag(cat.crit_indirect_shares) || truthyFlag(cat.crit_combined_shares)) {
    critParts.push('A');
  }
  if (truthyFlag(cat.crit_direct_voting) || truthyFlag(cat.crit_indirect_voting) || truthyFlag(cat.crit_combined_voting)) {
    critParts.push('B');
  }
  if (truthyFlag(cat.crit_c)) critParts.push('C');
  if (truthyFlag(cat.crit_d)) critParts.push('D');
  if (truthyFlag(cat.crit_e)) critParts.push('E');
  if (truthyFlag(cat.crit_f)) critParts.push('F');

  const parts: string[] = [];
  if (typeParts.length) parts.push(typeParts.join(' / '));
  if (critParts.length) parts.push(`Criteria ${critParts.join(', ')}`);
  return parts.join(' · ');
}

function formatCriteriaAPercent(slot: BoAppendixRegisterSlot): string {
  const c = slot.criteria || {};
  const p = slot.percents || {};
  if (!(truthyFlag(c.crit_direct_shares) || truthyFlag(c.crit_indirect_shares) || truthyFlag(c.crit_combined_shares))) {
    return '';
  }
  if (truthyFlag(c.cat_both) || truthyFlag(c.crit_combined_shares)) {
    return pctLabel(p.pct_combined_shares || p.pct_direct_shares || p.pct_indirect_shares);
  }
  if (truthyFlag(c.cat_indirect) || truthyFlag(c.crit_indirect_shares)) {
    return pctLabel(p.pct_indirect_shares);
  }
  return pctLabel(p.pct_direct_shares || p.pct_combined_shares || p.pct_indirect_shares);
}

function formatCriteriaBPercent(slot: BoAppendixRegisterSlot): string {
  const c = slot.criteria || {};
  const p = slot.percents || {};
  if (!(truthyFlag(c.crit_direct_voting) || truthyFlag(c.crit_indirect_voting) || truthyFlag(c.crit_combined_voting))) {
    return '';
  }
  if (truthyFlag(c.cat_both) || truthyFlag(c.crit_combined_voting)) {
    return pctLabel(p.pct_combined_voting || p.pct_direct_voting || p.pct_indirect_voting);
  }
  if (truthyFlag(c.cat_indirect) || truthyFlag(c.crit_indirect_voting)) {
    return pctLabel(p.pct_indirect_voting);
  }
  return pctLabel(p.pct_direct_voting || p.pct_combined_voting || p.pct_indirect_voting);
}

function smReasonLabel(id: number): string {
  if (id === 1) return 'No beneficial owner';
  if (id === 2) return 'Cannot be identified';
  if (id === 3) return 'In the process of obtaining ownership information';
  return '';
}

function pushRow(rows: RegisterDetailRow[], label: string, value: unknown) {
  const v = String(value ?? '').trim();
  if (!v) return;
  rows.push({ label, value: v });
}

function buildBoAppendixPanels(source: Record<string, any>): RegisterDetailTabPanel[] {
  const preset = {
    state: source.state || source.presetForm?.state || {
      values: source.values || source.presetForm?.values || {},
      addresses: source.addresses || source.presetForm?.addresses || {},
      criteriaByDoc: source.criteriaByDoc || source.presetForm?.criteriaByDoc || {},
    },
    includedDocs: source.includedDocs || source.presetForm?.includedDocs || [],
    values: source.values || source.presetForm?.values || source.state?.values || {},
    addresses: source.addresses || source.presetForm?.addresses || source.state?.addresses || {},
    criteriaByDoc: source.criteriaByDoc || source.presetForm?.criteriaByDoc || source.state?.criteriaByDoc || {},
  };

  const slots = collectBoAppendixRegisterSlots(preset);
  const companyName = String(
    source.companyName || source.entityName || source.presetForm?.companyName || '',
  ).trim();
  const companyRegNo = String(
    source.companyRegistrationNo || source.entityRegNo || source.presetForm?.companyRegistrationNo || '',
  ).trim();

  if (!slots.length) {
    // 레거시 fallback — 이름만이라도 있으면 단일 패널
    const legacyName = String(
      source.boFullName || source.smFullName || source.memberName || '',
    ).trim();
    if (!legacyName) return [];
    const rows: RegisterDetailRow[] = [];
    pushRow(rows, 'Company Name', companyName);
    pushRow(rows, 'Registration No.', companyRegNo);
    pushRow(rows, 'Full Name', legacyName);
    return [{ tabTitle: legacyName, rows }];
  }

  return slots.map((slot, i) => {
    const docLabel = docLabelForBoAppendix(slot.docCode);
    const rows: RegisterDetailRow[] = [];
    pushRow(rows, 'Company Name', companyName);
    pushRow(rows, 'Registration No.', companyRegNo);
    pushRow(rows, 'Document', docLabel);
    pushRow(rows, slot.isSeniorManagement ? 'Senior Management' : 'BO Full Name', slot.name);
    pushRow(rows, 'ID / NRIC / Passport / Reg. No.', slot.idNo);

    if (!slot.isCompany) {
      pushRow(rows, 'Date of Birth', slot.dob);
      pushRow(rows, 'Nationality', slot.nationality);
      pushRow(rows, 'Race', slot.race);
      pushRow(rows, 'Gender', slot.gender);
    }

    pushRow(rows, 'Email', slot.email);
    pushRow(rows, 'Contact No.', slot.phone);
    pushRow(rows, 'Position in the Company', slot.occupation);
    pushRow(
      rows,
      slot.isSeniorManagement ? 'SM Appointment Date' : 'Date Became BO',
      slot.becomingDate,
    );
    pushRow(rows, 'Residential Address', formatAddr(slot.residentialAddress));
    pushRow(
      rows,
      slot.isSeniorManagement ? 'Office Address' : 'Business Address',
      formatAddr(slot.businessAddress),
    );

    if (!slot.isSeniorManagement) {
      pushRow(rows, 'BO Type / Criteria', formatCriteriaSummary(slot));
      pushRow(rows, 'Criteria A (shares) %', formatCriteriaAPercent(slot));
      pushRow(rows, 'Criteria B (voting) %', formatCriteriaBPercent(slot));
    } else {
      pushRow(rows, 'SM Reason', smReasonLabel(slot.smReasonId));
    }

    if (!slot.contactCd) {
      pushRow(rows, 'Contact', 'Will be created on register (manual entry)');
    }

    const shortName = slot.name.length > 28 ? `${slot.name.slice(0, 26)}…` : slot.name;
    return {
      tabTitle: slots.length > 1 ? `${i + 1}. ${shortName}` : shortName || docLabel,
      rows,
    };
  });
}

registerRegisterDetail('M32109', {
  title: 'Beneficial Owner Letters (Appendix A/B/C) Details',
  fields: [
    // buildPanels 가 우선 — fields 는 패널 빌드 실패 시 fallback
    { label: 'Company Name', key: ['companyName', 'entityName', 'presetForm.companyName'] },
    { label: 'Registration No.', key: ['companyRegistrationNo', 'entityRegNo', 'presetForm.companyRegistrationNo'] },
    { label: 'BO Full Name', key: ['boFullName', 'state.values.bo_full_name', 'state.values.ba02_bo_full_name', 'state.values.ba03_bo_full_name'] },
    { label: 'Senior Management', key: ['smFullName', 'state.values.ba05_sm_full_name', 'state.values.ba04_sm_full_name'] },
  ],
  buildPanels: buildBoAppendixPanels,
});
